import NoImage from 'assets/images/others/no-image.png';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { decompressFromBase64 } from 'lz-string';
import React, { FC, Suspense, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { commitLocalUpdate, graphql, useLazyLoadQuery, useMutation, useRelayEnvironment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { useDialogManager } from 'src/components/widgets/dialog';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import {
    SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY,
    ShopifyDuplicationWarningDialog,
    shouldShowShopifyDuplicationWarningForPublish
} from 'src/components/widgets/shopifyDuplicationWarning/shopifyDuplicationWarning';
import { useRelayStore } from 'src/relay/environment';
import config from 'src/shared/config';
import { T, backofficeUrl, isDraftProduct, toGlobalID, toLocalID } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import usePermissions from 'src/shared/hooks/usePermissions';
import { DisconnectProductSalesChannel } from '../productSalesChannelList';
import type { productSidePanelContent_DisableBulkVariationMutation } from './__generated__/productSidePanelContent_DisableBulkVariationMutation.graphql';
import type { productSidePanelContent_ProductQuery } from './__generated__/productSidePanelContent_ProductQuery.graphql';
import { productSidePanelContent_RemoveAIStagingMutation } from './__generated__/productSidePanelContent_RemoveAIStagingMutation.graphql';
import { productSidePanelContent_RemoveAIVirtualModelMutation } from './__generated__/productSidePanelContent_RemoveAIVirtualModelMutation.graphql';
import { productSidePanelContent_RemoveAreaBgMutation } from './__generated__/productSidePanelContent_RemoveAreaBgMutation.graphql';
import { productSidePanelContent_RemoveGridRuleMutation } from './__generated__/productSidePanelContent_RemoveGridRuleMutation.graphql';
import { productSidePanelContent_RemoveMoqMutation } from './__generated__/productSidePanelContent_RemoveMoqMutation.graphql';
import { productSidePanelContent_RemovePricingMutation } from './__generated__/productSidePanelContent_RemovePricingMutation.graphql';
import { productSidePanelContent_RemoveProfanityMutation } from './__generated__/productSidePanelContent_RemoveProfanityMutation.graphql';
import type { productSidePanelContent_RemoveQuoteRuleMutation } from './__generated__/productSidePanelContent_RemoveQuoteRuleMutation.graphql';
import type { productSidePanelContent_SetStatusProductMutation } from './__generated__/productSidePanelContent_SetStatusProductMutation.graphql';
import type { productSidePanelContent_ShopifyCustomizationStrategyQuery } from './__generated__/productSidePanelContent_ShopifyCustomizationStrategyQuery.graphql';
import type { productSidePanelContent_ToolDataQuery } from './__generated__/productSidePanelContent_ToolDataQuery.graphql';
import type { productSidePanelContent_UnassignPrintMethodMutation } from './__generated__/productSidePanelContent_UnassignPrintMethodMutation.graphql';
import type { productSidePanelContent_UnassignShapesMutation } from './__generated__/productSidePanelContent_UnassignShapesMutation.graphql';
import type { productSidePanelContent_UpdateQuoteRuleMutation } from './__generated__/productSidePanelContent_UpdateQuoteRuleMutation.graphql';
import type { productSidePanelContent_AssignQuoteRuleMutation } from './__generated__/productSidePanelContent_AssignQuoteRuleMutation.graphql';
import type { productSidePanelContent_AllProductsForUnassignQuery } from './__generated__/productSidePanelContent_AllProductsForUnassignQuery.graphql';
import {
    ProductSalesChannelInfo,
    TOOL_DEFINITIONS,
    getAllSalesChannels,
    getSalesChannelCount,
    getToolInfo,
    getToolLabel,
    resolveMenuToolCategories
} from './common/constants';
import { useTreeMenu } from 'src/components/widgets/pageBlocksList/helpers';
import { ZakekeEnvironment } from 'src/shared/enums';
import {
    DraftBadge,
    FooterButton,
    HeaderMeta,
    HeaderPreviewButton,
    HeaderSep,
    PanelFooter,
    PanelHeaderContent,
    PanelHeaderImg,
    PanelHeaderInfo,
    SidePanelDrawer,
    SidePanelScrollArea,
    StatusBadge,
    ToolBadge
} from './common/styled';
import type { AddPanelKey, ProductSidePanelProps, ProductToolData, SidePanelProduct, StackedEditor, ToolDefinition } from './common/types';
import { AddRulePanels } from './sections/addRulePanels';
import { ConfiguratorStructureSection } from './sections/configuratorStructureSection';
import { DeleteConfirmationOverlay } from './sections/deleteConfirmationOverlay';
import { FullScreenOverview } from './sections/fullScreenOverview';
import { HelpArticlePanel } from './sections/helpArticlePanel';
import { CustomizerStructureSection } from './sections/productStructureSection';
import { StackedEditorPanel } from './sections/stackedEditorPanel';
import { StatusBarSection } from './sections/statusBarSection';
import ToolCategorySections from './sections/toolCategorySections';
import { ViewerStructureSection } from './sections/viewerStructureSection';

// ========== Queries ==========
const sidePanelProductQuery = graphql`
    query productSidePanelContent_ProductQuery(
        $id: ID!
        $skipProductPrintTypes: Boolean!
        $skipProductScenes: Boolean!
        $skipProductAreaBackgroundRules: Boolean!
        $skipProductQuoteRule: Boolean!
        $skipProductMoqRules: Boolean!
    ) {
        model(id: $id) {
            id
            modelID
            name
            code
            statusID
            configurationStatus
            smallImageUrl
            zakekeEnvironment
            provider
            canAddAdditionalCode
            sourceProductTemplateID
            modelColorsCompressed
            salesChannel {
                id
                detail {
                    id
                    properties {
                        name
                    }
                }
                ecommerceSettings {
                    ecommerceName
                    remoteUrl
                    productsApi
                    hasProductsApi
                    definitiveUrl
                }
            }
            additionalCodes {
                code
                salesChannelID
                merchantProductName
                salesChannel {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        productsApi
                        hasProductsApi
                        definitiveUrl
                    }
                }
            }
            printTypes @skip(if: $skipProductPrintTypes) {
                printTypeID
                name
                printTypeRestrictions {
                    disableSellerImages
                    disableUserImages
                }
            }
            scenes @skip(if: $skipProductScenes) {
                sceneID
            }
            areaBackgroundRules @skip(if: $skipProductAreaBackgroundRules) {
                productRule {
                    colorList
                }
                printTypeRule {
                    key
                    value {
                        ruleID
                    }
                }
            }
            quoteRule @skip(if: $skipProductQuoteRule) {
                id
                quoteRuleID
                name
                isValidForAllProducts
                formControlsData
                allowAddToCart
                notifyEmailList
                products
            }
            moqRules @skip(if: $skipProductMoqRules) {
                productRule {
                    id
                    moqRuleID
                    name
                }
                printTypeRules {
                    key
                    value {
                        moqRuleID
                        name
                    }
                }
            }
        }
        subscription {
            maxProducts {
                numPublishedProducts
            }
        }
    }
`;

const sidePanelToolDataQuery = graphql`
    query productSidePanelContent_ToolDataQuery(
        $productId: Long!
        $modelId: ID!
        $pricingFirst: Int!
        $skipTemplates: Boolean!
        $skipPresetConfigurations: Boolean!
        $skipShapes: Boolean!
        $skipPricing: Boolean!
        $skipProfanity: Boolean!
        $skipAI: Boolean!
        $skipCollage: Boolean!
        $skipAreaBackground: Boolean!
        $skipExtraOptions: Boolean!
        $skipPrintingMethods: Boolean!
        $skipPlacement: Boolean!
        $skipBulkVariations: Boolean!
        $skipMoq: Boolean!
        $skipQuote: Boolean!
        $skipImages: Boolean!
        $skipExtensionFields: Boolean!
    ) {
        designTemplates(productId: $productId) @skip(if: $skipTemplates) {
            designTemplateId
            name
            previewUrl
        }
        presetConfigurationsByProduct(productID: $modelId) @skip(if: $skipPresetConfigurations) {
            id
            compositionID
            name
            previewImageUrl
        }
        shapesAssignmentsForProduct(productID: $productId) @skip(if: $skipShapes) {
            forProduct
            printTypesIDs
        }
        shapesAssignments @skip(if: $skipShapes) {
            productsIDs
            printTypesIDs
        }
        hasShapes @skip(if: $skipShapes)
        shapesCount @skip(if: $skipShapes)
        pricingsDetailed(productID: $productId, first: $pricingFirst) @skip(if: $skipPricing) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                }
            }
        }
        profanityFilterRulesForProduct(productID: $productId) @skip(if: $skipProfanity) {
            productRule {
                ruleID
                ruleName
            }
            printTypeRules {
                key
                value {
                    ruleID
                    ruleName
                }
            }
        }
        aiProductStagingRulesByProduct(productID: $productId) @skip(if: $skipAI) {
            id
            name
            productIDs
            printTypeIDs
        }
        aiVirtualModelRulesByProduct(productID: $productId) @skip(if: $skipAI) {
            id
            name
            productIDs
            printTypeIDs
        }
        gridRulesByProduct(productID: $productId) @skip(if: $skipCollage) {
            id
            name
        }
        areaBackgroundRulesByProduct(productID: $productId) @skip(if: $skipAreaBackground) {
            id
            name
        }
        additionalAttributesByProduct(productID: $modelId) @skip(if: $skipExtraOptions) {
            id
            additionalAttributeID
            name
        }
        myAdditionalAttributes(status: 0) @skip(if: $skipExtraOptions) {
            id
        }
        profanityFilterRulesCount @skip(if: $skipProfanity)
        printTypes(first: 200) @skip(if: $skipPrintingMethods) {
            edges {
                node {
                    id
                    printTypeID
                    name
                }
            }
        }
        pricings(first: 200) @skip(if: $skipPricing) {
            edges {
                node {
                    id
                    pricingName
                    pricingID
                    printTypes {
                        printTypeID
                        printTypeName
                    }
                    products {
                        productID
                    }
                }
            }
        }
        areaBackgroundRulesCount @skip(if: $skipAreaBackground)
        areaBackgroundRules @skip(if: $skipAreaBackground) {
            areaBackgroundRuleID
            name
        }
        gridRulesCount @skip(if: $skipCollage)
        placementsOutput(id: $modelId) @skip(if: $skipPlacement) {
            id
            placementOutputID
            name
            isEnabled
        }
        imageGeneration @skip(if: $skipAI) {
            id
        }
        designWithAISetting @skip(if: $skipAI) {
            settingsID
        }
        productMultipleVariantsRule(productId: $modelId) @skip(if: $skipBulkVariations) {
            attributeCodes
            nameAndNumberEnabled
            skipOptionForNameAndNumber
            bulkAsNameAndNumberEnabled
            enableImportCSV
            options
        }
        aiProductStagingRulesCount @skip(if: $skipAI)
        aiVirtualModelRulesCount @skip(if: $skipAI)
        moqRulesCount @skip(if: $skipMoq)
        quoteRulesCount @skip(if: $skipQuote)
        quoteRulesList @skip(if: $skipQuote) {
            id
            quoteRuleID
            name
        }
        imageMacroCategories(onlybackground: false) @skip(if: $skipImages) {
            hasImages
        }
        extensionFields @skip(if: $skipExtensionFields) {
            id
            name
        }
    }
`;

const allProductsForUnassignQuery = graphql`
    query productSidePanelContent_AllProductsForUnassignQuery {
        modelsHierarchy {
            productID
        }
    }
`;

interface QuoteUnassignAllProductsLoaderProps {
    pending: {
        quoteRuleID: number;
        name: string;
        formControlsData: string;
        allowAddToCart: boolean;
        notifyEmailList: string;
        productGlobalId: string;
        productLocalId: number;
    };
    onDone: () => void;
    onFail: (error: Error) => void;
}

const QuoteUnassignAllProductsLoader: FC<QuoteUnassignAllProductsLoaderProps> = ({ pending, onDone, onFail }) => {
    const data = useLazyLoadQuery<productSidePanelContent_AllProductsForUnassignQuery>(allProductsForUnassignQuery, {});
    const [commitUpdateQuoteRule] = useMutation<productSidePanelContent_UpdateQuoteRuleMutation>(graphql`
        mutation productSidePanelContent_UpdateQuoteRuleMutation($input: QuoteRuleInput!) {
            quoteRuleUpdate(input: $input) {
                id
                isValidForAllProducts
                products
            }
        }
    `);
    const firedRef = useRef(false);

    useEffect(() => {
        if (firedRef.current) return;
        firedRef.current = true;

        const otherProductIds = (data?.modelsHierarchy || [])
            .map((m) => toLocalID(m?.productID))
            .filter((id): id is number => id != null && id !== pending.productLocalId);

        commitUpdateQuoteRule({
            variables: {
                input: {
                    quoteRuleID: pending.quoteRuleID,
                    name: pending.name,
                    formControlsData: pending.formControlsData,
                    allowAddToCart: pending.allowAddToCart,
                    notifyEmailList: pending.notifyEmailList,
                    isValidForAllProducts: false,
                    products: otherProductIds
                }
            },
            updater: (store) => {
                const productRecord = store.get(pending.productGlobalId);
                if (productRecord) {
                    productRecord.setValue(null, 'quoteRule');
                }
            },
            onCompleted: onDone,
            onError: onFail
        });
    }, []);

    return null;
};

const shopifyCustomizationStrategyQuery = graphql`
    query productSidePanelContent_ShopifyCustomizationStrategyQuery {
        ecommerceSettingsAdditionalDataForEcommercePlatform(ecommerce: SHOPIFY) {
            customizationStrategy
        }
    }
`;

export const ProductSidePanelContent: FC<ProductSidePanelProps> = ({
    productId,
    onClose,
    onDelete,
    onPreview,
    onPublishToggle,
    onDuplicate,
    onAddSalesChannel,
    canAddSalesChannelFromPublishedMiniSite = false
}) => {
    // ========== Hooks ==========
    const navigate = useNavigate();
    const relayEnvironment = useRelayEnvironment();
    const { showDialog } = useDialogManager();
    const { isAllowed } = usePermissions();
    const { sellerStaticsDetail } = useAccountData();
    const shopifyCustomizationStrategyData = useLazyLoadQuery<productSidePanelContent_ShopifyCustomizationStrategyQuery>(shopifyCustomizationStrategyQuery, {});

    // ========== UI State ==========
    const [channelsOpen, setChannelsOpen] = useState(false);
    const [expandAllKey, setExpandAllKey] = useState(0);
    const [openSections, setOpenSections] = useState<Set<string>>(new Set());
    const [hideUnconfigured, setHideUnconfigured] = useState(() => {
        try {
            return localStorage.getItem('zakeke_hideUnconfiguredTools') === 'true';
        } catch {
            return false;
        }
    });
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [optimisticStatusId, setOptimisticStatusId] = useState<number | null>(null);
    const [isPublishing, setIsPublishing] = useState(false);

    // ========== Panel & Editor State ==========
    const [stackedEditor, setStackedEditor] = useState<StackedEditor | null>(null);
    const [activeAddPanel, setActiveAddPanel] = useState<AddPanelKey | null>(null);
    // Locally reflects a Get a quote rule just assigned from the sidebar so the popover updates
    // instantly without a store invalidation / page refresh (null = follow server data).
    const [assignedQuoteRuleOverride, setAssignedQuoteRuleOverride] = useState<{ id: string; name: string } | null>(null);
    const [helpPanel, setHelpPanel] = useState<{ articleId: string; helpUrl: string } | null>(null);

    // ========== Data Refresh State ==========
    const [dataRefreshKey, setDataRefreshKey] = useState(0);
    const [unassigningItemId, setUnassigningItemId] = useState<string | null>(null);
    const [isUnassigning, startUnassignTransition] = useTransition();
    const [pendingQuoteUnassign, setPendingQuoteUnassign] = useState<{
        quoteRuleID: number;
        name: string;
        formControlsData: string;
        allowAddToCart: boolean;
        notifyEmailList: string;
        productGlobalId: string;
        productLocalId: number;
    } | null>(null);

    // ========== Mutations ==========

    const [commitSetStatus] = useMutation<productSidePanelContent_SetStatusProductMutation>(graphql`
        mutation productSidePanelContent_SetStatusProductMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on LiquidSintaxError {
                        message
                    }
                }
            }
        }
    `);

    const [commitUnassignPrintMethod] = useMutation<productSidePanelContent_UnassignPrintMethodMutation>(graphql`
        mutation productSidePanelContent_UnassignPrintMethodMutation($input: AddOrRemoveAssignmentPrintTypeToProductsInput!) {
            addOrRemoveAssignmentPrintTypeToProducts(input: $input)
        }
    `);

    const [commitRemovePricing] = useMutation<productSidePanelContent_RemovePricingMutation>(graphql`
        mutation productSidePanelContent_RemovePricingMutation($pricingId: ID!, $productIds: [Long!]!) {
            removeProductsFromPricing(pricingId: $pricingId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitAssignQuoteRule] = useMutation<productSidePanelContent_AssignQuoteRuleMutation>(graphql`
        mutation productSidePanelContent_AssignQuoteRuleMutation($ruleId: ID!, $productIds: [Long!]!) {
            assignProductsToQuoteRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveMoq] = useMutation<productSidePanelContent_RemoveMoqMutation>(graphql`
        mutation productSidePanelContent_RemoveMoqMutation($moqRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromMoqRule(moqRuleId: $moqRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIStaging] = useMutation<productSidePanelContent_RemoveAIStagingMutation>(graphql`
        mutation productSidePanelContent_RemoveAIStagingMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIStagingRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAIVirtualModel] = useMutation<productSidePanelContent_RemoveAIVirtualModelMutation>(graphql`
        mutation productSidePanelContent_RemoveAIVirtualModelMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAIVirtualModelRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveProfanity] = useMutation<productSidePanelContent_RemoveProfanityMutation>(graphql`
        mutation productSidePanelContent_RemoveProfanityMutation($profanityRuleId: ID!, $productIds: [Long!]!) {
            removeProductsFromProfanityRule(profanityRuleId: $profanityRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveGridRule] = useMutation<productSidePanelContent_RemoveGridRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveGridRuleMutation($gridRuleId: ID!, $productIds: [Long!]) {
            removeProductsFromGridRule(gridRuleId: $gridRuleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveAreaBg] = useMutation<productSidePanelContent_RemoveAreaBgMutation>(graphql`
        mutation productSidePanelContent_RemoveAreaBgMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromAreaBackgroundRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveQuoteRule] = useMutation<productSidePanelContent_RemoveQuoteRuleMutation>(graphql`
        mutation productSidePanelContent_RemoveQuoteRuleMutation($ruleId: ID!, $productIds: [Long!]!) {
            removeProductsFromQuoteRule(ruleId: $ruleId, productIds: $productIds) {
                result
            }
        }
    `);

    const [commitRemoveExtraOption] = useMutation(graphql`
        mutation productSidePanelContent_RemoveExtraOptionMutation($additionalAttributeID: ID!, $productID: ID!) {
            removeProductFromAdditionalAttribute(additionalAttributeID: $additionalAttributeID, productID: $productID) {
                id
                additionalAttributeID
            }
        }
    `);

    const [commitDisableBulkVariation] = useMutation<productSidePanelContent_DisableBulkVariationMutation>(graphql`
        mutation productSidePanelContent_DisableBulkVariationMutation($id: ID!, $input: ProductMultipleVariantsRuleInput!) {
            updateProductMultipleVariantsRule(productId: $id, input: $input) {
                attributeCodes
                nameAndNumberEnabled
                skipOptionForNameAndNumber
                bulkAsNameAndNumberEnabled
                enableImportCSV
                options
            }
        }
    `);

    const [commitUnassignShapes] = useMutation<productSidePanelContent_UnassignShapesMutation>(graphql`
        mutation productSidePanelContent_UnassignShapesMutation($productsIds: [ID!]!, $printTypesIds: [ID!]!) {
            assignShapes(productsIds: $productsIds, printTypesIds: $printTypesIds)
        }
    `);

    const [commitEnablePlacement] = useMutation(graphql`
        mutation productSidePanelContent_EnablePlacementMutation($id: ID!, $isEnabled: Boolean!) {
            enablePlacementOutput(id: $id, isEnabled: $isEnabled) {
                placementOutput {
                    id
                    isEnabled
                }
            }
        }
    `);

    const canAccessPrintingMethods = isAllowed(['PRINTING_METHODS']);
    const canAccessImages = isAllowed(['IMAGES']);
    const canAccessTemplates = isAllowed(['CUSTOMIZER_PRE_DESIGNED_TEMPLATES']);
    const canAccessPricing = isAllowed(['PRICING_RULES']);
    const canAccess3DPreview = isAllowed(['CUSTOMIZER_3D_PREVIEW']);
    const canAccessPlacement = isAllowed(['ADVANCED_PRINT_FILES']);
    const canAccessBulkVariations = isAllowed(['BULK_VARIATION_FORMS']);
    const canAccessExtraOptions = isAllowed(['EXTRA_OPTIONS']);
    const canAccessQuoteSettings = isAllowed(['QUOTES_SETTINGS']);
    const canAccessMoq = isAllowed(['MOQ']);
    const canAccessAI = isAllowed(['AI_CREATIVE_TOOLS']);
    const canAccessExtensionFields = isAllowed(['EXTENSION_FIELDS']);
    const canAccessCollage = isAllowed(['COLLAGES']);
    const canAccessAreaBackground = isAllowed(['CUSTOMIZER_PRODUCT_BACKGROUND']);
    const canAccessPresetConfigurations = isAllowed(['PRE_SET_CONFIGURATIONS']);
    const canAccessProfanity = isAllowed(['PROFANITY']);
    const canAccessShapes = isAllowed(['SHAPES']);
    const canReadProductPrintTypes = canAccessPrintingMethods || canAccessImages || canAccessShapes;

    // ========== Data Queries ==========
    const data = useLazyLoadQuery<productSidePanelContent_ProductQuery>(
        sidePanelProductQuery,
        {
            id: productId,
            skipProductPrintTypes: !canReadProductPrintTypes,
            skipProductScenes: !canAccess3DPreview,
            skipProductAreaBackgroundRules: !canAccessAreaBackground,
            skipProductQuoteRule: !canAccessQuoteSettings,
            skipProductMoqRules: !canAccessMoq
        },
        { fetchPolicy: 'network-only', fetchKey: dataRefreshKey }
    );
    const product = data.model as SidePanelProduct;

    // ========== Derived Data (Product) ==========

    const allProductColors = React.useMemo(() => {
        const compressed = product?.modelColorsCompressed;
        if (!compressed) return [];
        try {
            return JSON.parse(decompressFromBase64(compressed)) as {
                colorID: number;
                name: string;
                code: string | null;
                colorCode: string | null;
                sides?: {
                    sideID?: number;
                    name?: string;
                    imageUrl?: string;
                    ppcm?: number;
                    areas?: { name?: string; boundsWidth?: number; boundsHeight?: number }[];
                }[];
            }[];
        } catch {
            return [];
        }
    }, [product?.modelColorsCompressed]);

    const productColors = React.useMemo(() => allProductColors.filter((color) => color.colorID !== 1), [allProductColors]);

    const productStructureSummary = React.useMemo(() => {
        const totalVariants = productColors.length;
        const source = allProductColors.length > 0 ? allProductColors : [];
        const maxSides = source.reduce((max, color) => Math.max(max, color.sides?.length || 0), 0);
        const maxPrintAreas = source.reduce((max, color) => {
            const variantAreas = (color.sides || []).reduce((sum, side) => sum + (side.areas?.length || 0), 0);
            return Math.max(max, variantAreas);
        }, 0);
        return { totalVariants, maxSides, maxPrintAreas };
    }, [productColors, allProductColors]);

    const toolDataQuery = useLazyLoadQuery<productSidePanelContent_ToolDataQuery>(
        sidePanelToolDataQuery,
        {
            productId: product.modelID,
            modelId: product.id,
            pricingFirst: 50,
            skipTemplates: !canAccessTemplates,
            skipPresetConfigurations: !canAccessPresetConfigurations,
            skipShapes: !canAccessShapes,
            skipPricing: !canAccessPricing,
            skipProfanity: !canAccessProfanity,
            skipAI: !canAccessAI,
            skipCollage: !canAccessCollage,
            skipAreaBackground: !canAccessAreaBackground,
            skipExtraOptions: !canAccessExtraOptions,
            skipPrintingMethods: !canAccessPrintingMethods,
            skipPlacement: !canAccessPlacement,
            skipBulkVariations: !canAccessBulkVariations,
            skipMoq: !canAccessMoq,
            skipQuote: !canAccessQuoteSettings,
            skipImages: !canAccessImages,
            skipExtensionFields: !canAccessExtensionFields
        },
        { fetchPolicy: 'network-only', fetchKey: dataRefreshKey }
    );

    // ========== Derived Data (Tool Data) ==========

    const accountPrintMethods = useMemo(() => {
        const edges = toolDataQuery?.printTypes?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            numericId: String(edge.node.printTypeID),
            name: edge.node.name
        }));
    }, [toolDataQuery]);

    const accountPricingRules = useMemo(() => {
        const edges = toolDataQuery?.pricings?.edges || [];
        return edges.map((edge) => ({
            id: edge.node.id,
            name: edge.node.pricingName || `#${edge.node.pricingID}`
        }));
    }, [toolDataQuery]);

    const accountHasRules = useMemo<Record<string, boolean>>(
        () => ({
            'printing-methods': (accountPrintMethods?.length ?? 0) > 0,
            pricing: (accountPricingRules?.length ?? 0) > 0,
            profanity: (toolDataQuery?.profanityFilterRulesCount ?? 0) > 0,
            'area-background': (toolDataQuery?.areaBackgroundRulesCount ?? 0) > 0,
            collage: (toolDataQuery?.gridRulesCount ?? 0) > 0,
            'ai-lifestyle-scenes': (toolDataQuery?.aiProductStagingRulesCount ?? 0) > 0,
            'ai-try-on': (toolDataQuery?.aiVirtualModelRulesCount ?? 0) > 0,
            moq: (toolDataQuery?.moqRulesCount ?? 0) > 0,
            'get-a-quote': (toolDataQuery?.quoteRulesCount ?? 0) > 0,
            'extra-options': (toolDataQuery?.myAdditionalAttributes?.length ?? 0) > 0
        }),
        [toolDataQuery, accountPrintMethods, accountPricingRules]
    );

    const placements = useMemo(
        () =>
            (toolDataQuery?.placementsOutput || []).map((p) => ({
                id: p.id,
                name: p.name ?? '',
                isEnabled: p.isEnabled
            })),
        [toolDataQuery]
    );
    const aiImageGenConfigured = useMemo(() => {
        return toolDataQuery?.imageGeneration != null;
    }, [toolDataQuery]);
    const aiDesignGenConfigured = useMemo(() => toolDataQuery?.designWithAISetting != null, [toolDataQuery]);
    const bulkVariationConfigured = useMemo(() => (toolDataQuery?.productMultipleVariantsRule?.attributeCodes || []).length > 0, [toolDataQuery]);

    const toolData = React.useMemo<ProductToolData>(() => {
        const moqRule = product.moqRules?.productRule;
        // ===== Shared print-type inheritance helpers =====
        // Resolve a printing method's display name from its ID (numeric or string form).
        const printMethodName = (id: number | string) => (product.printTypes || []).find((pt) => String(pt.printTypeID) === String(id))?.name || `#${id}`;
        // The product's own printing methods — a rule is inherited only when it targets one of these.
        const productPrintTypeIds = new Set((product.printTypes || []).map((pt) => String(pt.printTypeID)));
        const productModelId = String(product.modelID);
        const inheritedMeta = (source: string) => `${T._('Inherited from printing method', 'Admin')} ${source}`;
        // Classify a rule (that exposes its own product/print-type assignments) against THIS product's
        // current state. Three outcomes:
        //   - direct: assigned to the product itself → { inherited: false }
        //   - inherited: not direct, but targets one of the product's current printing methods → meta
        //   - null: neither → no longer applies to the product (e.g. right after its printing method
        //     was unassigned) and must NOT be shown, otherwise it would look directly associated.
        const classifyByAssignment = (
            assignedProductIds: readonly (number | string)[],
            assignedPrintTypeIds: readonly (number | string)[]
        ): { inherited: boolean; meta?: string } | null => {
            if (assignedProductIds.some((pid) => String(pid) === productModelId)) return { inherited: false };
            const matchPt = assignedPrintTypeIds.find((ptId) => productPrintTypeIds.has(String(ptId)));
            return matchPt != null ? { inherited: true, meta: inheritedMeta(printMethodName(matchPt)) } : null;
        };
        // Account pricing rules keyed by pricingID → its product/print-type assignments. Used to
        // classify each of the product's applicable pricing rules, because `pricingsDetailed`
        // returns rules assigned both directly and via an associated printing method.
        const pricingAssignmentById = new Map((toolDataQuery.pricings?.edges ?? []).map((edge) => [String(edge.node.pricingID), edge.node]));
        return {
            loaded: true,
            templates: (toolDataQuery.designTemplates || []).map((template) => ({
                id: template.designTemplateId.toString(),
                name: template.name,
                previewUrl: template.previewUrl
            })),
            presetConfigurations: (toolDataQuery.presetConfigurationsByProduct || []).map((preset) => ({
                id: preset.id,
                name: preset.name || `#${preset.compositionID}`,
                previewUrl: preset.previewImageUrl ?? null
            })),
            // Directly assigned to this product only. Shapes inherited via an associated printing
            // method are surfaced through `inheritedRuleInfo.shapes` instead (read-only).
            shapesConfigured: !!((toolDataQuery.shapesCount ?? 0) > 0 && toolDataQuery.shapesAssignmentsForProduct?.forProduct),
            hasShapes: (toolDataQuery.shapesCount ?? 0) > 0,
            // The product's applicable pricing rules (direct + inherited via a printing method).
            // Inherited ones are flagged read-only with the printing method they come from; rules
            // that no longer apply (neither direct nor via a current printing method) are dropped.
            pricingRules: (toolDataQuery.pricingsDetailed?.edges || []).flatMap((edge) => {
                const assignment = pricingAssignmentById.get(String(edge.node.pricingID));
                // Without assignment data we can't determine inheritance — keep it as a direct rule.
                const cls = assignment
                    ? classifyByAssignment(
                          (assignment.products ?? []).map((p) => p!.productID),
                          (assignment.printTypes ?? []).map((pt) => pt!.printTypeID)
                      )
                    : { inherited: false };
                if (!cls) return [];
                return [{ id: edge.node.id, name: edge.node.pricingName || `#${edge.node.pricingID}`, inherited: cls.inherited, meta: cls.meta }];
            }),
            profanityRules: (() => {
                const rule = toolDataQuery.profanityFilterRulesForProduct?.productRule;
                if (!rule) return [];
                return [{ id: toGlobalID(rule.ruleID, 'ProfanityRule'), name: rule.ruleName || `#${rule.ruleID}` }];
            })(),
            // Applicable AI rules (direct + inherited via a printing method). Inherited ones are
            // flagged read-only with the printing method they come from; rules that no longer apply
            // (neither direct nor via a current printing method) are dropped.
            aiStagingRules: (toolDataQuery.aiProductStagingRulesByProduct || []).flatMap((rule) => {
                const cls = classifyByAssignment(rule.productIDs ?? [], rule.printTypeIDs ?? []);
                return cls ? [{ id: rule.id, name: rule.name, inherited: cls.inherited, meta: cls.meta }] : [];
            }),
            aiVirtualModelRules: (toolDataQuery.aiVirtualModelRulesByProduct || []).flatMap((rule) => {
                const cls = classifyByAssignment(rule.productIDs ?? [], rule.printTypeIDs ?? []);
                return cls ? [{ id: rule.id, name: rule.name, inherited: cls.inherited, meta: cls.meta }] : [];
            }),
            has3DScene: !!(product.scenes?.length && product.scenes[0]?.sceneID),
            areaBackgroundRules: (toolDataQuery.areaBackgroundRulesByProduct || []).map((rule) => ({
                id: rule.id,
                name: rule.name || ''
            })),
            // Rules active on the product because they are inherited from an associated printing
            // method (not assigned directly). These tools keep their product-level and per-print-type
            // assignments in separate fields, so inherited rules never appear in the direct list above
            // and are surfaced here instead (pricing and the AI tools flag inherited items inline).
            inheritedRuleInfo: (() => {
                // AreaBackgroundRule (in printTypeRule) carries no name, so resolve it from the
                // account rule list by matching its ruleID.
                const areaBgRuleName = new Map((toolDataQuery.areaBackgroundRules || []).map((r) => [r.areaBackgroundRuleID, r.name]));
                // Keep only print types that actually carry a rule — the backend may return
                // placeholder entries with a null value for print types without an assignment.
                return {
                    'area-background': (product.areaBackgroundRules?.printTypeRule ?? [])
                        .filter((r) => r.value)
                        .map((r) => ({
                            name: (r.value && areaBgRuleName.get(r.value.ruleID)) || `#${r.value?.ruleID}`,
                            source: printMethodName(r.key)
                        })),
                    moq: (product.moqRules?.printTypeRules ?? [])
                        .filter((r) => r.value)
                        .map((r) => ({
                            name: r.value?.name || `#${r.value?.moqRuleID}`,
                            source: printMethodName(r.key)
                        })),
                    profanity: (toolDataQuery.profanityFilterRulesForProduct?.printTypeRules ?? [])
                        .filter((r) => r.value)
                        .map((r) => ({
                            name: r.value?.ruleName || `#${r.value?.ruleID}`,
                            source: printMethodName(r.key)
                        })),
                    // Shapes have no named rule — surface the tool itself with the printing method it comes from.
                    shapes: (toolDataQuery.shapesAssignmentsForProduct?.forProduct ? [] : (toolDataQuery.shapesAssignmentsForProduct?.printTypesIDs ?? [])).map(
                        (ptId) => ({ name: T._('Shapes and masks', 'Admin'), source: printMethodName(ptId) })
                    )
                };
            })(),
            quoteRule: product.quoteRule ? { id: product.quoteRule.id, name: product.quoteRule.name || `#${product.quoteRule.quoteRuleID}` } : null,
            moqRules: moqRule ? [{ id: moqRule.id, name: moqRule.name || `#${moqRule.moqRuleID}` }] : [],
            collageRules: (toolDataQuery.gridRulesByProduct || []).filter(Boolean).map((rule) => ({
                id: rule!.id!,
                name: rule!.name || ''
            })),
            bulkVariationEnabled: bulkVariationConfigured,
            extraOptions: toolDataQuery.additionalAttributesByProduct
                ? [
                      {
                          id: toolDataQuery.additionalAttributesByProduct.id,
                          name: toolDataQuery.additionalAttributesByProduct.name || `#${toolDataQuery.additionalAttributesByProduct.additionalAttributeID}`
                      }
                  ]
                : [],
            placements,
            aiImageGenConfigured,
            aiDesignGenConfigured,
            imageRestrictions: (product.printTypes || []).map((pt) => ({
                printMethodId: String(pt.printTypeID),
                printMethodName: pt.name,
                userUpload: !(pt.printTypeRestrictions?.disableUserImages ?? false),
                clipartGalleries: !(pt.printTypeRestrictions?.disableSellerImages ?? false)
            })),
            hasGalleryContent: (toolDataQuery.imageMacroCategories || []).some((cat) => cat.hasImages),
            extensionFields: (toolDataQuery.extensionFields || []).map((ef) => ({
                id: ef!.id,
                name: ef!.name || `#${ef!.id}`
            }))
        };
    }, [toolDataQuery, product, placements, aiImageGenConfigured, aiDesignGenConfigured, bulkVariationConfigured]);

    // Tools available for this product's environment.
    const availableTools = useMemo(() => (product ? TOOL_DEFINITIONS.filter((t) => t.environments.includes(product.zakekeEnvironment)) : []), [product]);

    // Group and order the sidebar tool categories exactly like the navigation menu:
    // the treeMenu (fetched for this product's environment) is the single source of truth.
    const menuEnvironment =
        product?.zakekeEnvironment === 'COMPOSER'
            ? ZakekeEnvironment.Composer
            : product?.zakekeEnvironment === 'VIEWER'
              ? ZakekeEnvironment.Viewer
              : ZakekeEnvironment.Customizer;
    const menuTree = useTreeMenu(menuEnvironment);
    // Overview keeps every tool grouped by menu; the scroll sidebar pulls "Get a quote" out
    // into a pinned block near the sales channels, so its category grouping excludes it.
    const overviewCategories = useMemo(() => resolveMenuToolCategories(menuTree, availableTools), [menuTree, availableTools]);
    const sidebarCategories = useMemo(
        () =>
            resolveMenuToolCategories(
                menuTree,
                availableTools.filter((tool) => tool.key !== 'get-a-quote')
            ),
        [menuTree, availableTools]
    );
    const quoteTool = useMemo(() => availableTools.find((tool) => tool.key === 'get-a-quote'), [availableTools]);

    if (!product) return null;

    // ========== Computed Values ==========
    const isOnline = (optimisticStatusId ?? product.statusID) === 1;
    // A draft (incomplete) product is always unpublished and cannot be published until completed.
    const isDraft = !isOnline && isDraftProduct(product.configurationStatus);
    const salesChannels = getAllSalesChannels(product);
    const salesChannelCount = getSalesChannelCount(product);
    const isToolAllowed = (tool: ToolDefinition) => isAllowed(tool.permissions);
    const isShopify = product.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY';
    const isDuplicateEnabled =
        product.zakekeEnvironment !== 'VIEWER' && product.provider === 'ZAKEKE' && product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE';
    const hasSalesChannels = salesChannelCount > 0;
    const canAddSalesChannel =
        !!onAddSalesChannel &&
        product.canAddAdditionalCode &&
        (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || product.additionalCodes.length >= 1 || canAddSalesChannelFromPublishedMiniSite) &&
        product.provider === 'ZAKEKE' &&
        product.zakekeEnvironment !== 'COMPOSER';
    const salesChannelAction = canAddSalesChannel ? onAddSalesChannel : !hasSalesChannels && onPublishToggle ? onPublishToggle : undefined;
    const salesChannelActionLabel = canAddSalesChannel ? (
        <>
            <Icon>
                <PlusIcon />
            </Icon>
            {T._('Add to a Sales Channel', 'Admin')}
        </>
    ) : !hasSalesChannels ? (
        isOnline ? (
            T._('Unpublish', 'Admin')
        ) : (
            T._('Publish', 'Admin')
        )
    ) : null;

    const hasOptionPrice = (opt: { price: number | null; optionCode: string }): boolean => {
        if (!isShopify) {
            return !!(opt.price && opt.price > 0);
        }

        try {
            const parsed = JSON.parse(opt.optionCode);
            return parsed?.zakekePlatform === true;
        } catch {
            return false;
        }
    };

    // ========== Event Handlers ==========

    const handleEditProduct = () => {
        if (product.zakekeEnvironment === 'COMPOSER') {
            window.location.href = `/Composer?id=${product.modelID}&page=product`;
        } else if (product.zakekeEnvironment === 'VIEWER') {
            window.location.href = '/' + config.culture + '/Admin/DAM?page=/gallery';
        } else {
            navigate(backofficeUrl('/product?product_id=' + product.modelID));
        }
    };

    const handleOpenToolEditor = (toolKey: string, itemId: string) => {
        setStackedEditor({ type: toolKey, name: toolKey, id: itemId });
    };

    const handleEnablePlacement = (placementId: string, isEnabled: boolean) => {
        commitEnablePlacement({
            variables: { id: placementId, isEnabled },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => setDataRefreshKey((k) => k + 1)
        });
    };

    const handleCloseStacked = () => {
        const closedToolKey = stackedEditor?.type;
        setStackedEditor(null);

        // Mutations inside the embedded editor's iframe run in a separate Relay environment,
        // so the parent's useLazyLoadQuery fetchKey doesn't reliably pick up new data.
        // For tools whose data is shown in the parent products list (today: printing methods)
        // we recreate the entire Relay environment to force every query to refetch.
        // For tools that only appear inside the drawer (image gallery, fill background, etc.)
        // we just invalidate the store and bump the drawer's refresh key — no full page reload.
        if (closedToolKey === 'printing-methods') {
            useRelayStore.getState().incrementRelayStoreKey();
        } else {
            commitLocalUpdate(relayEnvironment, (store) => store.invalidateStore());
            startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
        }
    };

    const sidebarNavState = { fromSidebar: true, sidebarProductId: productId };

    const handleDisconnectSalesChannel = (channel: ProductSalesChannelInfo) => {
        showDialog(
            'delete-channel',
            <DisconnectProductSalesChannel
                productID={product.id}
                sku={channel.productCode}
                salesChannelID={channel.salesChannelID}
                onRemoveCompleted={() => {
                    commitLocalUpdate(relayEnvironment, (store) => store.invalidateStore());
                    setDataRefreshKey((k) => k + 1);
                }}
            />
        );
    };

    const handleGoToFullTool = (route: string, id?: string) => {
        if (id) {
            navigate(backofficeUrl(route + '/' + id), { state: sidebarNavState });
        } else {
            navigate(backofficeUrl(route), { state: sidebarNavState });
        }
    };

    const maybeShowShopifyDuplicationWarning = (publishedShopifyProduct: boolean, isFirstPublishedProduct: boolean) => {
        if (!publishedShopifyProduct) return;
        if (!shouldShowShopifyDuplicationWarningForPublish(sellerStaticsDetail?.registeredOn, isFirstPublishedProduct)) return;
        if (localStorage.getItem(SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY) === 'true') return;

        const isDuplication = shopifyCustomizationStrategyData.ecommerceSettingsAdditionalDataForEcommercePlatform?.some(
            (settings) => settings.customizationStrategy === 'DUPLICATION'
        );
        if (!isDuplication) return;

        showDialog('shopify-duplication-warning', <ShopifyDuplicationWarningDialog />);
    };

    const handlePublishToggle = async () => {
        if (isPublishing) return;

        // A draft (incomplete) product cannot be published; it must be completed first. Unpublishing stays allowed.
        if (!isOnline && isDraft) {
            showErrorNotification({ message: T._('Complete the product configuration before publishing it.', 'Admin') });
            return;
        }

        if (!hasSalesChannels && onPublishToggle) {
            onPublishToggle();
            return;
        }

        const newStatusId = (optimisticStatusId ?? product.statusID) === 1 ? 0 : 1;
        const isFirstPublishedProduct = (data.subscription?.maxProducts.numPublishedProducts ?? 0) === 0;
        setOptimisticStatusId(newStatusId);
        setIsPublishing(true);
        commitSetStatus({
            variables: { input: { productID: product.id, status: newStatusId === 1 ? 'PUBLISHED' : 'UN_PUBLISHED' } },
            onCompleted: () => {
                setIsPublishing(false);
                maybeShowShopifyDuplicationWarning(newStatusId === 1 && isShopify, isFirstPublishedProduct);
            },
            onError: () => {
                setOptimisticStatusId(product.statusID);
                setIsPublishing(false);
            }
        });
    };

    const handleUnassignTool = (toolKey: string, itemId: string, _itemName: string) => {
        setUnassigningItemId(itemId);

        const onDone = () => {
            setUnassigningItemId(null);
            startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
        };

        const updater = (store: any) => store.invalidateStore();

        const onFail = (error: Error) => {
            console.error(`Failed to unassign ${toolKey}:`, error);
            setUnassigningItemId(null);
        };

        if (toolKey === 'printing-methods') {
            commitUnassignPrintMethod({
                variables: { input: { printTypeID: itemId, productIDsToRemove: [product.id] } },
                updater,
                onCompleted: onDone,
                onError: onFail
            });
        } else if (toolKey === 'pricing') {
            commitRemovePricing({ variables: { pricingId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'moq') {
            commitRemoveMoq({ variables: { moqRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'ai-lifestyle-scenes') {
            commitRemoveAIStaging({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'ai-try-on') {
            commitRemoveAIVirtualModel({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'profanity') {
            commitRemoveProfanity({ variables: { profanityRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'collage') {
            commitRemoveGridRule({ variables: { gridRuleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'area-background') {
            commitRemoveAreaBg({ variables: { ruleId: itemId, productIds: [product.modelID] }, updater, onCompleted: onDone, onError: onFail });
        } else if (toolKey === 'get-a-quote') {
            const quoteRule = product.quoteRule;
            const quoteUpdater = (store: any) => {
                const productRecord = store.get(product.id);
                if (productRecord) {
                    productRecord.setValue(null, 'quoteRule');
                }
            };

            if (quoteRule?.isValidForAllProducts) {
                setPendingQuoteUnassign({
                    quoteRuleID: quoteRule.quoteRuleID,
                    name: quoteRule.name ?? '',
                    formControlsData: quoteRule.formControlsData ?? '',
                    allowAddToCart: quoteRule.allowAddToCart,
                    notifyEmailList: quoteRule.notifyEmailList ?? '',
                    productGlobalId: product.id,
                    productLocalId: product.modelID
                });
            } else {
                commitRemoveQuoteRule({
                    variables: { ruleId: itemId, productIds: [product.modelID] },
                    updater: quoteUpdater,
                    onCompleted: () => setUnassigningItemId(null),
                    onError: onFail
                });
            }
        } else if (toolKey === 'extra-options') {
            // Mirrors the MOQ pattern: the backend allows removing the last product, leaving the
            // rule with zero assignments (still manageable from the Extra Options page).
            commitRemoveExtraOption({
                variables: { additionalAttributeID: itemId, productID: product.id },
                updater,
                onCompleted: onDone,
                onError: onFail
            });
        }
    };

    const handleDisableBulkVariation = () => {
        const rule = toolDataQuery?.productMultipleVariantsRule;
        const existingOptions = rule?.options
            ? (() => {
                  try {
                      return JSON.parse(rule.options);
                  } catch {
                      return {};
                  }
              })()
            : {};
        const updatedOptions = { ...existingOptions, enabled: false, salesChannelSpecific: undefined };

        commitDisableBulkVariation({
            variables: {
                id: toGlobalID(product.modelID, 'Model'),
                input: {
                    attributeCodes: [],
                    nameAndNumberEnabled: rule?.nameAndNumberEnabled ?? false,
                    skipOptionForNameAndNumber: rule?.skipOptionForNameAndNumber ?? false,
                    bulkAsNameAndNumberEnabled: rule?.bulkAsNameAndNumberEnabled ?? false,
                    enableImportCSV: rule?.enableImportCSV ?? false,
                    options: JSON.stringify(updatedOptions)
                }
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    const handleAssignShapes = () => {
        const assignments = toolDataQuery?.shapesAssignments;
        const currentProducts = (assignments?.productsIDs as string[]) || [];
        const currentPrintTypes = (assignments?.printTypesIDs as string[]) || [];

        if (currentProducts.includes(product.id)) return;

        commitUnassignShapes({
            variables: {
                productsIds: [...currentProducts, product.id],
                printTypesIds: currentPrintTypes
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    const handleUnassignShapes = () => {
        const assignments = toolDataQuery?.shapesAssignments;
        const remainingProducts = ((assignments?.productsIDs as string[]) || []).filter((id) => id !== product.id);
        const remainingPrintTypes = ((assignments?.printTypesIDs as string[]) || []).filter(
            (id) => !(product.printTypes || []).some((pt) => toGlobalID(pt.printTypeID, 'PrintType') === id)
        );

        commitUnassignShapes({
            variables: {
                productsIds: remainingProducts,
                printTypesIds: remainingPrintTypes
            },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
            }
        });
    };

    // ========== Render Content ==========

    const headerContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                <img src={product.smallImageUrl || NoImage} alt={product.name} />
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const fullScreenHeaderContent = (
        <PanelHeaderContent>
            <PanelHeaderImg>
                <img src={product.smallImageUrl || NoImage} alt={product.name} />
            </PanelHeaderImg>
            <PanelHeaderInfo>
                <h2>{product.name}</h2>
                <HeaderMeta>
                    <span>{product.code}</span>
                    <HeaderSep>&middot;</HeaderSep>
                    <ToolBadge $type={product.zakekeEnvironment}>{getToolLabel(product.zakekeEnvironment)}</ToolBadge>
                    <StatusBadge $online={isOnline}>{isOnline ? T._('Online', 'Admin') : T._('Not published', 'Admin')}</StatusBadge>
                    {isDraft && <DraftBadge>{T._('Draft', 'Admin')}</DraftBadge>}
                    {salesChannelCount > 0 && (
                        <>
                            <HeaderSep>&middot;</HeaderSep>
                            <span>
                                {salesChannelCount} {salesChannelCount === 1 ? T._('Sales Channel', 'Admin') : T._('Sales Channels', 'Admin')}
                            </span>
                        </>
                    )}
                </HeaderMeta>
            </PanelHeaderInfo>
        </PanelHeaderContent>
    );
    const footerContent = (
        <PanelFooter>
            <FooterButton $primary onClick={handleEditProduct}>
                {T._('Edit Product', 'Admin')}
            </FooterButton>
            {onPublishToggle && (
                <FooterButton $publish={!isOnline} $unpublish={isOnline} onClick={handlePublishToggle} disabled={isPublishing}>
                    {isPublishing ? '...' : isOnline ? T._('Unpublish', 'Admin') : T._('Publish', 'Admin')}
                </FooterButton>
            )}
            {onDuplicate && (
                <FooterButton
                    disabled={!isDuplicateEnabled}
                    title={!isDuplicateEnabled ? T._('This product cannot be duplicated', 'Admin') : undefined}
                    onClick={() => {
                        if (!isDuplicateEnabled) return;
                        onClose();
                        onDuplicate();
                    }}
                >
                    {T._('Duplicate', 'Admin')}
                </FooterButton>
            )}
            {onDelete && (
                <FooterButton $danger onClick={() => setConfirmingDelete(true)}>
                    {T._('Delete', 'Admin')}
                </FooterButton>
            )}
        </PanelFooter>
    );

    // "Get a quote" is surfaced as a chip in the status bar (next to the sales channels) instead
    // of a full tool category. Clicking it opens a popover whose content depends on the state:
    // active rule → info + Edit/Unassign; account rules available → assign list + add new; none →
    // empty state + set up. Assignable rules come from the same account list the panel uses.
    const quoteRulesList = (toolDataQuery?.quoteRulesList || []).map((rule) => ({ id: rule.id, name: rule.name || `#${rule.quoteRuleID}` }));
    const effectiveQuoteRule = quoteTool && isToolAllowed(quoteTool) ? (assignedQuoteRuleOverride ?? toolData.quoteRule ?? null) : null;
    const getAQuoteChip =
        quoteTool && isToolAllowed(quoteTool)
            ? {
                  active: !!effectiveQuoteRule,
                  items: effectiveQuoteRule ? [effectiveQuoteRule] : [],
                  assignableRules: quoteRulesList.filter((rule) => rule.id !== effectiveQuoteRule?.id),
                  description: quoteTool.description,
                  helpUrl: quoteTool.helpUrl,
                  onEdit: (id: string) => handleOpenToolEditor('get-a-quote', id),
                  onUnassign: (id: string, name: string) => {
                      setAssignedQuoteRuleOverride(null);
                      handleUnassignTool('get-a-quote', id, name);
                  },
                  onGoToSettings: () => handleGoToFullTool('/tools/get-a-quote'),
                  // Assign without invalidating the store: reflect it locally so the popover
                  // updates instantly and stays open (no page refresh).
                  onAssign: (id: string) => {
                      const rule = quoteRulesList.find((r) => r.id === id);
                      commitAssignQuoteRule({
                          variables: { ruleId: id, productIds: [product.modelID] },
                          onCompleted: () => setAssignedQuoteRuleOverride(rule ?? null)
                      });
                  },
                  // Opens the rule configuration panel embedded in full screen, without leaving the sidebar.
                  onCreateNew: () => handleOpenToolEditor('get-a-quote', '')
              }
            : undefined;

    // Shared props for the tool sections, reused by the pinned "Get a quote" block and the
    // main categorized list so both stay wired to the same handlers/state.
    const toolSectionCommonProps = {
        product,
        toolData,
        accountPrintMethods: accountPrintMethods || [],
        accountPricingRules: accountPricingRules || [],
        accountHasRules,
        isToolAllowed,
        expandAllKey,
        openSections,
        hideUnconfigured,
        isUnassigning,
        unassigningItemId,
        onOpenSectionChange: (toolKey: string, isOpen: boolean) => {
            if (isOpen) {
                setOpenSections(new Set([toolKey]));
            } else {
                setOpenSections((prev) => {
                    const next = new Set(prev);
                    next.delete(toolKey);
                    return next;
                });
            }
        },
        onHideUnconfiguredChange: (checked: boolean) => {
            setHideUnconfigured(checked);
            try {
                localStorage.setItem('zakeke_hideUnconfiguredTools', String(checked));
            } catch {
                /* */
            }
        },
        onCollapseAll: () => {
            setOpenSections(new Set());
            setExpandAllKey((k) => k + 1);
        },
        onOpenToolEditor: handleOpenToolEditor,
        onUnassignTool: handleUnassignTool,
        onDisableBulkVariation: handleDisableBulkVariation,
        onAssignShapes: handleAssignShapes,
        onUnassignShapes: handleUnassignShapes,
        onShowAddPanel: setActiveAddPanel,
        onGoToFullTool: handleGoToFullTool,
        onNavigate: (url: string, state?: any) => navigate(url, { state: { ...state, ...sidebarNavState } }),
        onEnablePlacement: handleEnablePlacement,
        onShowHelpPanel: (url: string) => {
            const match = url.match(/\/articles\/(\d+)/);
            if (match) setHelpPanel({ articleId: match[1], helpUrl: url });
        }
    };

    return (
        <>
            <SidePanelDrawer
                isOpen={true}
                onClose={onClose}
                header={headerContent}
                headerActions={
                    // A draft (incomplete) product cannot be previewed until its configuration is completed.
                    onPreview && product.zakekeEnvironment !== 'VIEWER' && !isDraft ? (
                        <HeaderPreviewButton onClick={onPreview}>{T._('Preview', 'Admin')}</HeaderPreviewButton>
                    ) : undefined
                }
                showFullScreenButton={product.zakekeEnvironment !== 'VIEWER'}
                fullScreenHeader={fullScreenHeaderContent}
                footer={footerContent}
                isDimmed={!!stackedEditor || !!activeAddPanel || !!helpPanel}
                width={580}
                overlayBackground='rgba(0, 0, 0, 0.08)'
                skipAnimation
            >
                {({ isFullScreen }) =>
                    isFullScreen ? (
                        <FullScreenOverview
                            product={product}
                            productStructureSummary={productStructureSummary}
                            availableTools={availableTools}
                            orderedCategories={overviewCategories}
                            toolData={toolData}
                            isToolAllowed={isToolAllowed}
                        />
                    ) : (
                        <SidePanelScrollArea>
                            {product.zakekeEnvironment !== 'VIEWER' && (
                                <StatusBarSection
                                    zakekeEnvironment={product.zakekeEnvironment}
                                    isOnline={isOnline}
                                    salesChannelCount={getSalesChannelCount(product)}
                                    salesChannels={salesChannels}
                                    channelsOpen={channelsOpen}
                                    onToggleChannels={() => setChannelsOpen(!channelsOpen)}
                                    onSalesChannelAction={salesChannelAction}
                                    salesChannelActionLabel={salesChannelActionLabel}
                                    onDisconnectSalesChannel={handleDisconnectSalesChannel}
                                    getAQuote={getAQuoteChip}
                                />
                            )}
                            {product.zakekeEnvironment === 'COMPOSER' && (
                                <Suspense fallback={null}>
                                    <ConfiguratorStructureSection
                                        product={product}
                                        hasOptionPrice={hasOptionPrice}
                                        toolData={toolData}
                                        accountPricingRules={accountPricingRules || []}
                                        isToolAllowed={isToolAllowed}
                                        expandAllKey={expandAllKey}
                                        openSections={openSections}
                                        isUnassigning={isUnassigning}
                                        unassigningItemId={unassigningItemId}
                                        onOpenSectionChange={(toolKey, isOpen) => {
                                            if (isOpen) {
                                                setOpenSections(new Set([toolKey]));
                                            } else {
                                                setOpenSections((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(toolKey);
                                                    return next;
                                                });
                                            }
                                        }}
                                        onOpenToolEditor={handleOpenToolEditor}
                                        onUnassignTool={handleUnassignTool}
                                        onShowAddPanel={setActiveAddPanel}
                                        onGoToFullTool={handleGoToFullTool}
                                        onShowHelpPanel={(url) => {
                                            const match = url.match(/\/articles\/(\d+)/);
                                            if (match) setHelpPanel({ articleId: match[1], helpUrl: url });
                                        }}
                                    />
                                </Suspense>
                            )}
                            {product.zakekeEnvironment === 'CUSTOMIZER' && (
                                <CustomizerStructureSection
                                    productId={product.modelID}
                                    productStructureSummary={productStructureSummary}
                                    productColors={productColors}
                                    allProductColors={allProductColors}
                                    onManageProduct={() => {
                                        if (product.provider !== 'ZAKEKE') {
                                            handleGoToFullTool('/product?product_id=' + product.modelID);
                                            return;
                                        }
                                        handleGoToFullTool('/product/print-area/?product_id=' + product.modelID);
                                    }}
                                    onPrintAreaClick={(colorID, sideID) => {
                                        if (product.provider !== 'ZAKEKE') {
                                            handleGoToFullTool('/product?product_id=' + product.modelID);
                                            return;
                                        }
                                        handleGoToFullTool(
                                            productColors.length === 0
                                                ? `/product/print-area/?product_id=${product.modelID}&sideId=${sideID}`
                                                : `/product/variations/list/?product_id=${product.modelID}&variantId=${colorID}&sideId=${sideID}`
                                        );
                                    }}
                                />
                            )}
                            {product.zakekeEnvironment === 'VIEWER' && (
                                <Suspense fallback={null}>
                                    <ViewerStructureSection productId={product.id} />
                                </Suspense>
                            )}
                            <ToolCategorySections {...toolSectionCommonProps} availableTools={availableTools} orderedCategories={sidebarCategories} />
                            {confirmingDelete && (
                                <DeleteConfirmationOverlay
                                    productName={product.name}
                                    onCancel={() => setConfirmingDelete(false)}
                                    onConfirm={() => {
                                        setConfirmingDelete(false);
                                        onDelete!();
                                    }}
                                />
                            )}
                        </SidePanelScrollArea>
                    )
                }
            </SidePanelDrawer>
            <StackedEditorPanel
                stackedEditor={stackedEditor}
                product={product}
                toolDefinitions={TOOL_DEFINITIONS}
                onCloseStacked={handleCloseStacked}
                onGoToFullTool={handleGoToFullTool}
                onNavigate={(url) => navigate(backofficeUrl(url), { state: sidebarNavState })}
            />
            <AddRulePanels
                activeAddPanel={activeAddPanel}
                product={product}
                toolData={toolData}
                onCloseAddPanel={() => setActiveAddPanel(null)}
                onRefreshData={(_toolKey) => {
                    setTimeout(() => {
                        commitLocalUpdate(relayEnvironment, (store) => store.invalidateStore());
                        startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
                    }, 500);
                }}
                onNavigate={(url, state) => navigate(backofficeUrl(url), { state: { ...state, ...sidebarNavState } })}
                onOpenToolEditor={handleOpenToolEditor}
            />
            {helpPanel && <HelpArticlePanel articleId={helpPanel.articleId} helpUrl={helpPanel.helpUrl} onClose={() => setHelpPanel(null)} />}
            {pendingQuoteUnassign && (
                <Suspense fallback={null}>
                    <QuoteUnassignAllProductsLoader
                        pending={pendingQuoteUnassign}
                        onDone={() => {
                            setUnassigningItemId(null);
                            setPendingQuoteUnassign(null);
                            startUnassignTransition(() => setDataRefreshKey((k) => k + 1));
                        }}
                        onFail={(error) => {
                            console.error('Failed to unassign get-a-quote:', error);
                            setUnassigningItemId(null);
                            setPendingQuoteUnassign(null);
                        }}
                    />
                </Suspense>
            )}
        </>
    );
};
